// apps/web/src/components/decision-form/CertInfoCard.tsx
/**
 * @file CertInfoCard.tsx
 * @description 四、驗證證書資訊卡片。按 scheme 渲染欄位；證書有效期間依
 * computeCertValidity 自動連動計算（可手改）。
 *
 * 連動口徑（序37「證書有效期間規則系統自動生成並可手動修改」）：
 * - 初次：改「初次驗證日期」（firstAuditDate）→ validFrom = firstAuditDate、
 *   validUntil = firstAuditDate + 3年 - 1天，一併寫 validPeriod。
 * - 追蹤 / 展延：於 validPeriod 上方額外渲染「效期起日」DatePicker（綁 validFrom），
 *   改起始日期時以同一 3 年規則重算 validUntil（computeCertValidity），並寫 validPeriod。
 *   起始日與有效期間皆保持可手動修改，不鎖死；「一鍵帶入」既有非空值優先語義由上層維持。
 */
import { Card, DatePicker, Input, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { computeCertValidity, classifyVerificationType, type CertInfoField } from '@zhcb/shared';

interface Props {
  /** 證書欄位定義（來自 getDecisionFormDefs(scheme).certFields） */
  certFields: CertInfoField[];
  /** 案件驗證類型（用於有效期分類） */
  verificationType: string;
  /** 當前值 map */
  value: Record<string, string>;
  /** 變更回調 */
  onChange: (next: Record<string, string>) => void;
  /** 是否可編輯 */
  editable: boolean;
}

export function CertInfoCard({ certFields, verificationType, value, onChange, editable }: Props) {
  // 驗證類型分類：initial / surveillance / renewal
  const kind = classifyVerificationType(verificationType);
  // 追蹤 / 展延（非初次）需在 validPeriod 上方提供「效期起日」連動欄位
  const isNonInitial = kind !== 'initial';

  const setField = (key: string, v: string) => {
    const next = { ...value, [key]: v };
    // 僅「初次」案件在初次驗證日期變更時自動連動有效期間；
    // 追蹤/展延的有效期來自前證書（由「一鍵帶入」自動填充或手填），
    // 動初次日期不得清掉已帶入/已填的有效期。
    if (key === 'firstAuditDate' && kind === 'initial') {
      const { validFrom, validUntil } = computeCertValidity({
        kind: 'initial',
        firstAuditDate: v || null,
      });
      // 清空（v 為 ''）時一併清除有效期欄位；有值時寫入計算結果
      next.validFrom = validFrom ?? '';
      next.validUntil = validUntil ?? '';
      next.validPeriod = validFrom && validUntil ? `${validFrom} ~ ${validUntil}` : '';
    }
    onChange(next);
  };

  /**
   * 追蹤/展延：改「效期起日」（validFrom）時，以同一 3 年規則連動重算 validUntil。
   * 用 computeCertValidity 的 initial 分支即得「起日 + 3年 - 1天」，與初次口徑一致；
   * 起日清空時同步清除 validUntil / validPeriod。有效期欄位仍可手動再改（不鎖死）。
   */
  const setValidFrom = (v: string) => {
    const { validFrom, validUntil } = computeCertValidity({
      kind: 'initial',
      firstAuditDate: v || null,
    });
    onChange({
      ...value,
      validFrom: validFrom ?? '',
      validUntil: validUntil ?? '',
      validPeriod: validFrom && validUntil ? `${validFrom} ~ ${validUntil}` : '',
    });
  };

  return (
    <Card title="四、驗證證書資訊" size="small">
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        {certFields.map((f) => {
          if (f.key === 'firstAuditDate') {
            return (
              <div key={f.key}>
                <Typography.Text type="secondary">{f.label}（管理組填）</Typography.Text>
                <DatePicker
                  style={{ width: '100%' }}
                  disabled={!editable}
                  value={value[f.key] ? dayjs(value[f.key]) : null}
                  onChange={(d) => setField('firstAuditDate', d ? d.format('YYYY-MM-DD') : '')}
                />
              </div>
            );
          }
          // 追蹤/展延：在「證書有效期間」欄位前插入「效期起日」連動 DatePicker，
          // 改起日即以 3 年規則重算有效期間（與初次口徑一致，可再手動改）。
          if (f.key === 'validPeriod' && isNonInitial) {
            return (
              <div key={f.key}>
                <div>
                  <Typography.Text type="secondary">
                    效期起日（改此欄自動算有效期間）
                  </Typography.Text>
                  <DatePicker
                    style={{ width: '100%' }}
                    disabled={!editable}
                    value={value.validFrom ? dayjs(value.validFrom) : null}
                    onChange={(d) => setValidFrom(d ? d.format('YYYY-MM-DD') : '')}
                  />
                </div>
                <div style={{ marginTop: 8 }}>
                  <Typography.Text type="secondary">{f.label}</Typography.Text>
                  <Input
                    disabled={!editable}
                    value={value[f.key] ?? ''}
                    onChange={(e) => setField(f.key, e.target.value)}
                  />
                </div>
              </div>
            );
          }
          return (
            <div key={f.key}>
              <Typography.Text type="secondary">{f.label}</Typography.Text>
              <Input
                disabled={!editable}
                value={value[f.key] ?? ''}
                onChange={(e) => setField(f.key, e.target.value)}
              />
            </div>
          );
        })}
      </Space>
    </Card>
  );
}
